import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { IntegrationNoAccess } from '@/components/integration-no-access';
import { useExtension, useEnableExtension, useDisableExtension } from '@/hooks/use-extensions';
import { useLatestLinkedinParserRelease } from '@/hooks/use-linkedin-parser';
import { usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type { LinkedinParserRelease } from '@hygge/shared';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Columns3,
  Download,
  Linkedin,
  Loader2,
  XCircle,
} from 'lucide-react';

const routePath = '/settings/integrations/$id' as const;
export const Route = createFileRoute(routePath)({
  component: ExtensionConfigPage,
});

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  kanban: Columns3,
  clock: Clock,
};

const iconStyleMap: Record<string, string> = {
  'task-manager': 'bg-hygge/10 text-hygge',
  'time-tracker': 'bg-amber-500/10 text-amber-600',
};

const INSTALL_STEPS = [
  'Download the zip and unzip it to a folder you can keep around',
  'Open chrome://extensions in your browser',
  'Toggle Developer mode on (top-right corner)',
  'Click Load unpacked and pick the unzipped folder',
  'Pin "Hygge LinkedIn Parser" to the toolbar',
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ExtensionConfigPage() {
  const { id } = Route.useParams();
  const canUseLinkedin = usePermission('extensions.linkedin.use');
  const canManageIntegrations = usePermission('settings.general.manage');

  // LinkedIn Parser isn't in the `extensions` table — it's a hardcoded card with
  // a server-backed release artifact. Gated by extensions.linkedin.use.
  if (id === 'linkedin-parser') {
    return canUseLinkedin ? <LinkedinParserPage /> : <IntegrationNoAccess id={id} />;
  }

  // Every other integration is admin-managed — block direct-URL access for
  // LinkedIn-only recruiters. super_admin bypasses both checks.
  return canManageIntegrations ? <ServerExtensionConfigPage id={id} /> : <IntegrationNoAccess id={id} />;
}

function ServerExtensionConfigPage({ id }: { id: string }) {
  const { data: extension, isLoading, isError } = useExtension(id);
  const enableMutation = useEnableExtension();
  const disableMutation = useDisableExtension();

  const isToggling = enableMutation.isPending || disableMutation.isPending;

  if (isLoading) {
    return (
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          <BackLink />
          <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading extension...</span>
          </div>
        </div>
      </div>
    );
  }

  if (isError || !extension) {
    return (
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          <BackLink />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Extension Not Found
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              The extension &ldquo;{id}&rdquo; does not exist or is not available.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isEnabled = extension.status === 'enabled';
  const Icon = iconMap[extension.icon] ?? Columns3;
  const iconStyle = iconStyleMap[extension.id] ?? 'bg-hygge/10 text-hygge';

  function handleToggle() {
    if (!extension) return;
    if (isEnabled) {
      disableMutation.mutate(extension.id);
    } else {
      enableMutation.mutate(extension.id);
    }
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        <div className="space-y-4">
          <BackLink />

          <div className="flex items-start gap-4">
            <div
              className={cn('flex h-12 w-12 items-center justify-center rounded-full', iconStyle)}
            >
              <Icon className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                  {extension.name}
                </h1>
                <Badge
                  variant="outline"
                  className={cn(
                    'text-[11px] px-2 py-0.5 font-medium',
                    isEnabled
                      ? 'text-green-600 border-green-600/30 bg-green-600/5'
                      : 'text-muted-foreground border-border bg-muted/50'
                  )}
                >
                  {isEnabled && (
                    <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-current" />
                  )}
                  {isEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
                <span className="text-xs text-muted-foreground font-mono">
                  v{extension.version}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">{extension.description}</p>
            </div>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Extension Status</CardTitle>
            <CardDescription>
              {isEnabled
                ? 'This extension is active. Users with appropriate permissions can access its features.'
                : 'Enable this extension to make its features available to your organization.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isEnabled ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <p className="text-sm font-medium">
                    {isEnabled ? 'Extension is enabled' : 'Extension is disabled'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Installed {new Date(extension.installedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {isToggling && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <Switch checked={isEnabled} onCheckedChange={handleToggle} disabled={isToggling} />
              </div>
            </div>
          </CardContent>
        </Card>

        {extension.capabilities.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Capabilities</CardTitle>
              <CardDescription>Permissions granted when this extension is enabled</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {extension.capabilities.map((cap) => (
                  <Badge key={cap} variant="secondary" className="text-xs font-mono">
                    {cap}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {(enableMutation.isError || disableMutation.isError) && (
          <Card className="border-destructive/30 bg-destructive/5">
            <CardContent className="py-4">
              <p className="text-sm text-destructive">
                Failed to {isEnabled ? 'disable' : 'enable'} extension. Please try again.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function LinkedinParserPage() {
  const { data: release, isLoading } = useLatestLinkedinParserRelease();

  return (
    <div className="flex-1 overflow-auto">
      <div className="p-6 max-w-4xl mx-auto space-y-6">
        <div className="space-y-4">
          <BackLink />

          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/10 text-blue-600">
              <Linkedin className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                  LinkedIn Parser
                </h1>
                {release && (
                  <span className="text-xs text-muted-foreground font-mono">
                    v{release.version}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Chrome extension that parses LinkedIn profile pages and pushes candidates into
                HyggeOS.
              </p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading latest release...</span>
          </div>
        ) : (
          <InstallCard release={release ?? null} />
        )}
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/settings"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to Settings
    </Link>
  );
}

function InstallCard({ release }: { release: LinkedinParserRelease | null }) {
  const downloadButton = release ? (
    <Button asChild>
      <a href={release.downloadUrl} download>
        <Download className="h-4 w-4" />
        Download v{release.version}
      </a>
    </Button>
  ) : (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>
            <Button disabled>
              <Download className="h-4 w-4" />
              Download
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Download not yet available — ask an admin to publish the build
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Install</CardTitle>
        <CardDescription>
          The LinkedIn Parser ships as an unpacked Chrome extension. Download the latest build and
          load it once — Chrome keeps it around between sessions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          {downloadButton}
          {release && (
            <span className="text-xs text-muted-foreground">
              {formatFileSize(release.fileSize)} · published{' '}
              {new Date(release.uploadedAt).toLocaleDateString()}
            </span>
          )}
        </div>
        <ol className="list-decimal space-y-1.5 pl-5 text-sm text-muted-foreground">
          {INSTALL_STEPS.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
